<template>
  <div
    v-if="enabled"
    class="lang-switcher"
    :class="{ 'is-rtl': isRtl, 'is-busy': showLoading, 'is-dragging': dragging }"
    :style="{ left: pos.left + 'px', top: pos.top + 'px' }"
    data-no-translate="true"
  >
    <!-- 时区切换提示 -->
    <transition name="lang-tz-toast">
      <div v-if="tzToastVisible" class="lang-tz-toast" data-no-translate="true">
        <span class="lang-tz-toast__text">{{ tzToastText }}</span>
      </div>
    </transition>

    <!-- 圆形悬浮球（可拖动 + 可点击展开） -->
    <button
      ref="ball"
      type="button"
      class="lang-ball"
      :aria-label="showLoading ? '翻译进行中' : '切换语言'"
      :aria-busy="showLoading ? 'true' : 'false'"
      :disabled="interactionLocked && !visible"
      data-no-translate="true"
      @mousedown="onDragStart"
      @touchstart.passive="onDragStart"
      @click="onBallClick"
    >
      <!-- 用户主动切换语言期间：显示 loader；该轮完成后：显示地球图标 + 当前语言角标 -->
      <template v-if="showLoading">
        <span class="lang-ball__loader" data-no-translate="true"></span>
      </template>
      <template v-else>
        <svg
          class="lang-ball__globe"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
          data-no-translate="true"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18" />
          <path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18z" />
        </svg>
        <span class="lang-ball__badge" data-no-translate="true">{{ ballIcon }}</span>
      </template>
    </button>

    <!-- 选择浮层（基于球的位置自动选择弹出方向） -->
    <transition name="lang-fade">
      <div
        v-show="visible"
        class="lang-panel"
        :class="[
          'lang-panel--' + verticalSide,
          'lang-panel--' + horizontalSide,
        ]"
        data-no-translate="true"
        @click.stop
      >
        <div v-if="translateEnabled" class="lang-panel__title" data-no-translate="true">Language / 语言</div>
        <ul v-if="translateEnabled" class="lang-panel__list" role="listbox" data-no-translate="true">
          <li
            v-for="(label, code) in options"
            :key="code"
            class="lang-panel__item"
            :class="{
              'is-active': code === currentLang,
              'is-disabled': interactionLocked,
              'is-pending': showLoading && code === pendingLang,
            }"
            role="option"
            :aria-selected="code === currentLang ? 'true' : 'false'"
            :aria-disabled="interactionLocked ? 'true' : 'false'"
            :aria-busy="showLoading && code === pendingLang ? 'true' : 'false'"
            data-no-translate="true"
            @click="onSelect(code)"
          >
            <span class="lang-panel__name" data-no-translate="true">{{ label }}</span>
            <!-- 正在切换到该语言：显示 loading 图标 -->
            <span
              v-if="showLoading && code === pendingLang"
              class="lang-panel__spin"
              data-no-translate="true"
              aria-hidden="true"
            ></span>
            <!-- 当前已选中语言（非切换中）：显示 ✓ -->
            <span
              v-else-if="code === currentLang"
              class="lang-panel__check"
              data-no-translate="true"
            >✓</span>
          </li>
        </ul>

        <!-- 底部操作区：非中文时显示"重新翻译"按钮，供用户在译文异常时手动刷新 -->
        <div
          v-if="translateEnabled && currentLang !== SOURCE_LANG"
          class="lang-panel__footer"
          data-no-translate="true"
        >
          <button
            type="button"
            class="lang-panel__retry"
            :class="{ 'is-disabled': interactionLocked, 'is-loading': showLoading }"
            :disabled="interactionLocked"
            :aria-busy="showLoading ? 'true' : 'false'"
            data-no-translate="true"
            @click="onRetranslate"
          >
            <!-- 切换语言期间显示 loading 圆环；否则刷新图标 -->
            <span
              v-if="showLoading"
              class="lang-panel__spin lang-panel__spin--btn"
              data-no-translate="true"
              aria-hidden="true"
            ></span>
            <svg
              v-else
              class="lang-panel__retry-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M21 12a9 9 0 1 1-3.7-7.3" />
              <polyline points="21 3 21 9 15 9" />
            </svg>
            <span data-no-translate="true">{{ retranslateLabel }}</span>
          </button>
        </div>

        <!-- 时区切换区域（仅时区功能启用时显示） -->
        <div v-if="tzEnabled" class="lang-panel__tz" data-no-tz="true">
          <div class="lang-panel__tz-title" data-no-translate="true">Timezone</div>
          <div class="lang-panel__tz-tabs">
            <button
              type="button"
              class="lang-panel__tz-tab"
              :class="{ 'is-active': tzMode === 'beijing' }"
              data-no-translate="true"
              @click="onTzSelectBeijing"
            >北京时间</button>
            <button
              type="button"
              class="lang-panel__tz-tab"
              :class="{ 'is-active': tzMode === 'local' }"
              data-no-translate="true"
              @click="onTzSelectLocal"
            >{{ tzTimezone }}</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- 遮罩 -->
    <div
      v-show="visible"
      class="lang-mask"
      data-no-translate="true"
      @click="onMaskClick"
    ></div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  pageTranslator,
  getStoredLang,
  applyDir,
  isRTLLang,
  getTranslateMode,
  SOURCE_LANG,
} from '@/utils/pageTranslator';
import { canUseTranslate } from '@/utils/translateGate';

const CFG_KEYS = {
  INFO: 'multi_language_info',
  OPTIONS: 'multi_language_options',
  // 静态字典：按语言生成 key（中文不需要）
  DICT_PREFIX: 'multi_language_dict_',
  // 术语表 ID 列表（数组 JSON）：翻译 API 会自动读取该配置
  GLOSSARY_IDS: 'multi_language_glossary_ids',
};

// 悬浮球尺寸 + 视口边距 + 拖动判定阈值
// 注意：BALL_SIZE 必须与 <style> 中 @ball-size 保持一致，否则边界吸附会偏。
const BALL_SIZE = 40;
const SAFE_GAP = 12;
const DRAG_THRESHOLD = 5;
// 位置持久化 key（独立于翻译器自己的 LS）
const LS_POS_KEY = 'pageTranslator:ballPos';

// 时区 toast 会话级标记：整个会话只弹一次首次提示（模块级，跨组件实例共享）
let _tzFirstToastShownGlobal = false;

/**
 * 安全解析 JSON 字符串配置
 */
function safeParse(raw, fallback) {
  if (raw === undefined || raw === null || raw === '') return fallback;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
}

/** 获取视口尺寸（兼容老浏览器） */
function getViewport() {
  return {
    w: window.innerWidth || document.documentElement.clientWidth || 360,
    h: window.innerHeight || document.documentElement.clientHeight || 640,
  };
}

/** 把坐标限制在视口安全区内 */
function clampPos(left, top) {
  const { w, h } = getViewport();
  const maxLeft = w - BALL_SIZE - SAFE_GAP;
  const maxTop = h - BALL_SIZE - SAFE_GAP;
  return {
    left: Math.max(SAFE_GAP, Math.min(left, maxLeft)),
    top: Math.max(SAFE_GAP, Math.min(top, maxTop)),
  };
}

export default {
  name: 'LanguageSwitcher',
  props: {
    /** 翻译作用范围（DOM 根节点）。默认 document.body */
    rootEl: {
      type: null,
      default: null,
    },
    /** 当前工单的 ProcessDefinitionKey，用于命中 key 白名单 */
    processDefinitionKey: {
      type: String,
      default: '',
    },
    /**
     * 强制走用户灰度逻辑（useGray=1）。
     * 用于待办列表/已办列表等无流程标识的页面：
     * 必须系统语言非中文 || 用户在白名单，才开启翻译功能。
     */
    forceUserGray: {
      type: Boolean,
      default: false,
    },
    /** 时区功能是否启用 */
    tzEnabled: {
      type: Boolean,
      default: false,
    },
    /** 当前时区显示模式：'local' | 'beijing' */
    tzMode: {
      type: String,
      default: 'beijing',
    },
    /** 用户系统时区标识（如 'Asia/Tokyo'） */
    tzTimezone: {
      type: String,
      default: 'Asia/Shanghai',
    },
  },
  data() {
    return {
      visible: false,
      busy: false,            // 翻译进行中（含语言切换 + 增量补翻）
      switchLoading: false,   // 用户主动切换语言的翻译正在进行中（控制悬浮球 loading 动画）
      currentLang: SOURCE_LANG,
      pendingLang: '',        // 正在切换到的目标语言
      _unsubscribeBusy: null,
      initialized: false,
      isRtl: false,

      // ---- 时区 toast ----
      tzToastVisible: false,
      tzToastText: '',
      _tzToastTimer: null,
      _tzFirstToastShown: false, // 会话级：首次进入只提示一次

      // ---- 悬浮球位置/拖动 ----
      pos: { left: 0, top: 0 },
      dragging: false,
      _dragState: null,       // { startX, startY, startLeft, startTop, moved }
      _onDragMove: null,      // 绑定的移动监听
      _onDragEnd: null,       // 绑定的结束监听
      _onResize: null,        // 视口变化时重新约束
    };
  },
  computed: {
    ...mapGetters(['getConfigItem']),

    /** 可选语言列表（响应式） */
    options() {
      const opts = safeParse(this.getConfigItem(CFG_KEYS.OPTIONS), null);
      if (opts && typeof opts === 'object' && Object.keys(opts).length) return opts;
      return { 'zh-CN': '简体中文' };
    },

    /** 暴露给模板使用的源语言常量 */
    SOURCE_LANG() {
      return SOURCE_LANG;
    },

    /**
     * 悬浮球是否显示 loading 动画：
     *   - 用户每次主动切换语言（switchTo / retranslate）期间 → 显示 loading
     *   - 该轮翻译完成后（observer 增量翻译等后台活动）→ 不显示
     *   - 再切语言 → 再次显示 loading
     */
    showLoading() {
      return this.switchLoading;
    },

    /**
     * UI 是否禁止交互（面板打不开、按钮不能点）：
     *   - 首次翻译尚未完成时 → 锁定（避免初始化阶段被中断）
     *   - 首次翻译完成后 → 永远不锁，即使 switchLoading=true 也允许用户切换
     *     （旧流程靠 epoch 自动作废）
     */
    interactionLocked() {
      if (!this.switchLoading) return false;
      try {
        return !pageTranslator.isFirstTranslateDone();
      } catch (e) {
        return true; // 异常时保守锁定
      }
    },

    /**
     * "重新翻译"按钮文案：根据当前已翻译到的语言显示对应自然语言文案，
     * 不要混合显示中英两套（如 "Retranslate / 重新翻译"），避免在英/德等语言下出现中文。
     * 当前 currentLang 是中文时（按钮其实不会显示），兜底显示中文。
     */
    retranslateLabel() {
      const lang = this.currentLang || SOURCE_LANG;
      if (lang.startsWith('en')) return 'Retranslate';
      if (lang.startsWith('de')) return 'Erneut übersetzen';
      if (lang.startsWith('ar')) return 'إعادة الترجمة';
      if (lang.startsWith('ja')) return '再翻訳';
      if (lang.startsWith('ko')) return '다시 번역';
      if (lang.startsWith('fr')) return 'Retraduire';
      if (lang.startsWith('es')) return 'Volver a traducir';
      if (lang.startsWith('ru')) return 'Перевести снова';
      return '重新翻译';
    },

    /**
     * 是否启用翻译按钮：调用独立工具 canUseTranslate，
     * 综合判断流程白名单 + useGray 灰度策略 + 用户白名单 / 系统语言。
     * 详细规则见 src/utils/translateGate.js
     */
    enabled() {
      // 显式触达各配置项让 Vue 响应式追踪到（七彩石异步加载完会重算）
      // 多个表达式串成一个 void，让结果不影响返回值
      void this.getConfigItem('multi_language_info');
      void this.getConfigItem('useGray');
      void this.getConfigItem('multi_language_user');
      void this.getConfigItem(CFG_KEYS.GLOSSARY_IDS);
      const translateEnabled = canUseTranslate(this.$store, this.processDefinitionKey, {
        forceUserGray: this.forceUserGray,
      });
      // 翻译或时区任一启用 → 显示悬浮球
      return translateEnabled || this.tzEnabled;
    },

    /** 翻译功能是否启用（面板内翻译区域显隐用） */
    translateEnabled() {
      return canUseTranslate(this.$store, this.processDefinitionKey, {
        forceUserGray: this.forceUserGray,
      });
    },

    currentLabel() {
      return this.options[this.currentLang] || this.options[SOURCE_LANG] || '中文';
    },

    /** 球面图标：用当前语言代码前 2 字符（如 EN/DE/AR/中） */
    ballIcon() {
      const lang = this.currentLang || SOURCE_LANG;
      if (lang === 'zh-CN' || lang.startsWith('zh')) return '中';
      if (lang === 'ar-SA' || lang.startsWith('ar')) return 'ع';
      // 其他语言取代码前两位大写
      return (lang.split('-')[0] || 'EN').slice(0, 2).toUpperCase();
    },

    /** 面板垂直弹出方向：球在视口下半 → 向上弹（top），否则向下（bottom） */
    verticalSide() {
      const { h } = getViewport();
      return (this.pos.top + BALL_SIZE / 2) > h / 2 ? 'top' : 'bottom';
    },

    /** 面板水平弹出方向：球在视口右半 → 向左对齐（right），否则向右（left） */
    horizontalSide() {
      const { w } = getViewport();
      return (this.pos.left + BALL_SIZE / 2) > w / 2 ? 'right' : 'left';
    },
  },
  watch: {
    enabled: {
      immediate: true,
      handler(val) {
        // 调试：辅助定位"按钮没出现"
        // eslint-disable-next-line no-console
        console.log('[LanguageSwitcher] enabled =', val, {
          processDefinitionKey: this.processDefinitionKey,
          infoOpen: this.info && this.info.open,
          infoKey: this.info && this.info.key,
          inWhiteList: this.info && Array.isArray(this.info.key) && this.info.key.includes(this.processDefinitionKey),
        });
        this._tryInit();
      },
    },
    /** 时区功能启用时，首次弹一次提示 */
    tzEnabled: {
      immediate: true,
      handler(val) {
        if (val) this._onTzFirstEnabled();
      },
    },
    rootEl: {
      handler() {
        // 父组件 mounted 后才设置 rootEl，需要等它就绪后再初始化翻译器
        this._tryInit();
      },
    },
  },
  mounted() {
    // 调试：确认组件已挂载
    // eslint-disable-next-line no-console
    console.log('[LanguageSwitcher] mounted, props.processDefinitionKey =', this.processDefinitionKey);

    // 初始化球位置（恢复持久化或默认右下角；RTL 默认左下角）
    this._restoreBallPos();

    // 视口尺寸变化时重新约束位置（横竖屏切换、键盘弹出/收起等）
    this._onResize = () => {
      this.pos = clampPos(this.pos.left, this.pos.top);
    };
    window.addEventListener('resize', this._onResize);
    window.addEventListener('orientationchange', this._onResize);
  },
  beforeDestroy() {
    this.cleanupTranslator();
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('orientationchange', this._onResize);
      this._onResize = null;
    }
    // 兜底解绑可能仍在的 drag 监听
    this._teardownDragListeners();
  },
  methods: {
    /** enabled + rootEl 都就绪时才初始化翻译器 */
    _tryInit() {
      if (this.initialized) return;
      if (!this.enabled) return;
      if (!this.rootEl) return; // 父组件 mounted 后才会赋值
      this.$nextTick(() => this.initTranslator());
    },

    /** 读取目标语言的静态词典 */
    loadStaticDicts() {
      const dicts = {};
      Object.keys(this.options).forEach((lang) => {
        if (lang === SOURCE_LANG) return;
        const raw = this.getConfigItem(CFG_KEYS.DICT_PREFIX + lang);
        const dict = safeParse(raw, {});
        if (dict && typeof dict === 'object') dicts[lang] = dict;
      });
      return dicts;
    },

    /** 初始化翻译器并恢复持久化语言 */
    async initTranslator() {
      if (this.initialized) return;
      this.initialized = true;

      const root = this.rootEl || document.body;
      const staticDicts = this.loadStaticDicts();

      // 先确定要恢复的语言（在 init 前算好，让 observer 启动即按目标语言工作）
      const stored = getStoredLang();
      const initialLang = stored && this.options[stored] ? stored : SOURCE_LANG;

      // eslint-disable-next-line no-console
      console.log('[LanguageSwitcher] initTranslator', {
        rootTag: root && root.tagName,
        rootClass: root && root.className,
        stored,
        initialLang,
        staticDictKeys: Object.keys(staticDicts),
      });

      try {
        pageTranslator.init({
          root,
          options: this.options,
          staticDicts,
          initialLang,
        });
      } catch (e) {
        console.warn('[LanguageSwitcher] 翻译器初始化失败:', e);
        return;
      }

      // 订阅 busy 状态：仅"首次翻译"期间用于禁止切换；
      // 首次完成后 busy 仅做内部状态参考，UI 不展示 loading
      this._unsubscribeBusy = pageTranslator.onBusyChange((b) => {
        this.busy = b;
      });
      this.busy = pageTranslator.isBusy();

      // 同步 UI：当前语言、文本方向
      this.currentLang = initialLang;
      applyDir(initialLang, this.rootEl || null);
      this.isRtl = isRTLLang(initialLang);

      if (initialLang !== SOURCE_LANG) {
        // 恢复翻译：分多次重扫，覆盖 Vue 异步子组件渲染的不同时机
        this.switchLoading = true; // 恢复翻译也是一轮"主动切换"→ 显示 loading
        this.pendingLang = initialLang;
        try {
          // eslint-disable-next-line no-console
          console.log('[LanguageSwitcher] 开始恢复翻译为', initialLang);
          await pageTranslator.switchTo(initialLang);
          // eslint-disable-next-line no-console
          console.log('[LanguageSwitcher] 首轮翻译完成');
        } catch (e) {
          console.warn('[LanguageSwitcher] 恢复翻译（首轮）失败:', e);
        } finally {
          this.switchLoading = false;
          this.pendingLang = '';
        }
        // 持续兜底轮询（覆盖工单详情页的 AI 设计器组件等慢速 / 多次异步渲染）：
        // - 每 800ms 扫一次 root，持续最多 30 秒
        // - 命中缓存即时回写，新文本进 API
        // - 一旦扫到全部已翻译（无新增 API 请求），自动停止
        let stableCount = 0;
        const POLL_INTERVAL = 800;
        const MAX_DURATION_MS = 30000;
        const startTs = Date.now();
        this._restoreRetryInterval = setInterval(() => {
          if (Date.now() - startTs > MAX_DURATION_MS) {
            clearInterval(this._restoreRetryInterval);
            this._restoreRetryInterval = null;
            return;
          }
          if (pageTranslator.isBusy()) return;
          if (pageTranslator.currentLang === SOURCE_LANG) {
            clearInterval(this._restoreRetryInterval);
            this._restoreRetryInterval = null;
            return;
          }
          try {
            // 直接调内部翻译入口，缓存 / 已翻译标记会跳过重复项
            // 参与 busy 计数：让 UI 显示"翻译中"，避免轮询期间状态显示为已完成
            pageTranslator._incBgBusy();
            const promise = pageTranslator._translateRoot(pageTranslator.root, initialLang);
            // 连续 6 次（~5s）扫描没有新需要翻译的，认为页面稳定，停止轮询
            promise.then((touched) => {
              if (touched && touched.translated > 0) {
                stableCount = 0;
              } else {
                stableCount += 1;
                if (stableCount >= 6) {
                  clearInterval(this._restoreRetryInterval);
                  this._restoreRetryInterval = null;
                  // eslint-disable-next-line no-console
                  console.log('[LanguageSwitcher] 兜底轮询：页面已稳定，停止');
                }
              }
            }).catch(() => {}).finally(() => {
              pageTranslator._decBgBusy();
            });
          } catch (e) {
            // 入口同步抛错也要释放计数
            try { pageTranslator._decBgBusy(); } catch (err) { /* ignore */ }
          }
        }, POLL_INTERVAL);
      }
    },

    cleanupTranslator() {
      if (typeof this._unsubscribeBusy === 'function') {
        try { this._unsubscribeBusy(); } catch (e) {}
        this._unsubscribeBusy = null;
      }
      if (Array.isArray(this._restoreRetryTimers)) {
        this._restoreRetryTimers.forEach((t) => { try { clearTimeout(t); } catch (e) {} });
        this._restoreRetryTimers = null;
      }
      if (this._restoreRetryInterval) {
        try { clearInterval(this._restoreRetryInterval); } catch (e) {}
        this._restoreRetryInterval = null;
      }
      try { pageTranslator.destroy(); } catch (e) {}
      this.initialized = false;
    },

    onTriggerClick() {
      // 兼容旧调用入口（已不在 template 中使用，保留以防外部调用）
      this.onBallClick();
    },

    onMaskClick() {
      // 忙时也允许关闭面板（不会触发切换）
      this.visible = false;
    },

    // ---- 时区切换 ----
    onTzSelectLocal() {
      this.$emit('tz-switch-local');
      this._showTzToast(`页面时间已切换为 ${this.tzTimezone} 显示`);
      this.visible = false;
    },

    onTzSelectBeijing() {
      this.$emit('tz-switch-beijing');
      this._showTzToast('页面时间已切换为北京时间显示');
      this.visible = false;
    },

    /**
     * 显示时区 toast（自动消失）
     * 仅在首次进入 + 手动切换时调用，页面跳转恢复时不调用
     */
    _showTzToast(text, duration = 3000) {
      this.tzToastText = text;
      this.tzToastVisible = true;
      if (this._tzToastTimer) clearTimeout(this._tzToastTimer);
      this._tzToastTimer = setTimeout(() => { this.tzToastVisible = false; }, duration);
    },

    /**
     * 首次检测到时区功能启用时弹一次提示（会话级不重复）
     * 由 watch tzEnabled 触发
     */
    _onTzFirstEnabled() {
      if (_tzFirstToastShownGlobal) return;
      if (!this.tzEnabled) return;
      _tzFirstToastShownGlobal = true;
      // 延迟一点确保 tzTimezone prop 已更新
      setTimeout(() => {
        const tz = this.tzTimezone && this.tzTimezone !== 'Asia/Shanghai'
          ? this.tzTimezone
          : '';
        if (tz) {
          this._showTzToast(`页面时间已切换为 ${tz} 显示`, 4000);
        } else {
          this._showTzToast('时区转换功能已启用', 4000);
        }
      }, 500);
    },

    /* ---------------- 悬浮球位置 / 拖动 ---------------- */

    /** 恢复球位置（持久化优先；否则按 RTL 给默认值） */
    _restoreBallPos() {
      const { w, h } = getViewport();
      let left;
      let top;
      try {
        const raw = localStorage.getItem(LS_POS_KEY);
        if (raw) {
          const obj = JSON.parse(raw);
          if (obj && typeof obj.left === 'number' && typeof obj.top === 'number') {
            left = obj.left;
            top = obj.top;
          }
        }
      } catch (e) { /* ignore */ }

      if (typeof left !== 'number' || typeof top !== 'number') {
        // 默认：靠下，右侧（或 RTL 时左侧），距底部 30%
        const isRTL = isRTLLang(this.currentLang);
        left = isRTL ? SAFE_GAP : (w - BALL_SIZE - SAFE_GAP);
        top = h - BALL_SIZE - Math.max(SAFE_GAP, h * 0.25);
      }
      this.pos = clampPos(left, top);
    },

    _saveBallPos() {
      try {
        localStorage.setItem(LS_POS_KEY, JSON.stringify(this.pos));
      } catch (e) { /* ignore */ }
    },

    /** 拖动开始 */
    onDragStart(e) {
      // 仅"首次翻译进行中"时禁止拖动（与 onBallClick / onSelect 一致语义）；
      // 首次完成后即便后台 busy 也允许用户拖动悬浮球
      if (this.interactionLocked) return;
      const point = this._getEventPoint(e);
      if (!point) return;
      this._dragState = {
        startX: point.x,
        startY: point.y,
        startLeft: this.pos.left,
        startTop: this.pos.top,
        moved: false,
      };
      this.dragging = false; // 移动超阈值后再置 true，避免点击被误判
      this._setupDragListeners();
    },

    onDragMove(e) {
      if (!this._dragState) return;
      const point = this._getEventPoint(e);
      if (!point) return;
      const dx = point.x - this._dragState.startX;
      const dy = point.y - this._dragState.startY;

      if (!this._dragState.moved) {
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return; // 未达拖动阈值
        this._dragState.moved = true;
        this.dragging = true;
        // 拖动期间隐藏面板（视觉干净）
        if (this.visible) this.visible = false;
      }

      // 阻止页面滚动（仅在 touch 上有 cancelable 时）
      if (e.cancelable && e.preventDefault) {
        try { e.preventDefault(); } catch (err) { /* ignore */ }
      }

      this.pos = clampPos(
        this._dragState.startLeft + dx,
        this._dragState.startTop + dy
      );
    },

    onDragEnd() {
      const moved = this._dragState && this._dragState.moved;
      this._dragState = null;
      this._teardownDragListeners();
      if (moved) {
        // 真正发生过拖动：吸附到左右边的就近一侧
        this._snapToEdge();
        this._saveBallPos();
        // 关键：用 microtask 后再清 dragging，避免本次 mouseup 后立即触发 click
        setTimeout(() => { this.dragging = false; }, 0);
      } else {
        // 未发生拖动：直接清状态，让 click 正常触发
        this.dragging = false;
      }
    },

    /** 拖动结束后吸附到最近的左/右边界（更像 iOS 小白点） */
    _snapToEdge() {
      const { w } = getViewport();
      const center = this.pos.left + BALL_SIZE / 2;
      const targetLeft = center > w / 2
        ? (w - BALL_SIZE - SAFE_GAP)
        : SAFE_GAP;
      this.pos = clampPos(targetLeft, this.pos.top);
    },

    _setupDragListeners() {
      this._onDragMove = (e) => this.onDragMove(e);
      this._onDragEnd = () => this.onDragEnd();
      window.addEventListener('mousemove', this._onDragMove, { passive: false });
      window.addEventListener('mouseup', this._onDragEnd);
      window.addEventListener('touchmove', this._onDragMove, { passive: false });
      window.addEventListener('touchend', this._onDragEnd);
      window.addEventListener('touchcancel', this._onDragEnd);
    },

    _teardownDragListeners() {
      if (this._onDragMove) {
        window.removeEventListener('mousemove', this._onDragMove);
        window.removeEventListener('touchmove', this._onDragMove);
        this._onDragMove = null;
      }
      if (this._onDragEnd) {
        window.removeEventListener('mouseup', this._onDragEnd);
        window.removeEventListener('touchend', this._onDragEnd);
        window.removeEventListener('touchcancel', this._onDragEnd);
        this._onDragEnd = null;
      }
    },

    _getEventPoint(e) {
      if (!e) return null;
      if (e.touches && e.touches.length) {
        return { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
      if (e.changedTouches && e.changedTouches.length) {
        return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
      }
      return { x: e.clientX, y: e.clientY };
    },

    onBallClick() {
      // 拖动产生的 click 直接吞掉
      if (this.dragging) return;
      // 仅"首次翻译进行中"时不允许打开面板（避免初始化阶段被中断）；
      // 首次完成后（含重新翻译进行中、observer 增量翻译进行中等任意 busy 状态）
      // 都允许用户随时打开面板切换语言。
      if (this.interactionLocked && !this.visible) return;
      this.visible = !this.visible;
    },

    /**
     * Aegis 日志上报：上报用户每次切换语言。
     * 协议：
     *   - msg/trace : 'translate_lang_change'（事件名，便于 Aegis 后台筛选）
     *   - ext1      : 切换前语言（如 zh-CN）
     *   - ext2      : 切换后语言（如 en-US）
     *   - ext3      : 翻译模式 + 当前路由路径（如 "hunyuan-7b-500|/operateManage/business/details"）
     * try/catch 静默兜底：aegis 不存在或抛错都不影响业务。
     *
     * @param {string} fromLang 切换前语言
     * @param {string} toLang 切换后语言
     */
    /**
     * Aegis 日志上报：上报用户每次切换语言。
     *
     * 对齐项目中其他组件（如 ImageUploadInput.reportAegis）的统一写法：
     *   - 使用 aegis.infoAll，msg 用 JSON.stringify 包裹业务字段
     *   - ext1 用固定的场景标识，trace 带 action 便于 Aegis 后台筛选
     *   - 业务语义字段通过 msg 中的 name/ext1/ext2/ext3 表达（保留您定义的协议）
     *
     * 事件协议：
     *   - trace : 'translate_lang_change'
     *   - msg.name : 'translate_lang_change'
     *   - msg.ext1 : 切换前语言
     *   - msg.ext2 : 切换后语言
     *   - msg.ext3 : 翻译模式 + 当前路由路径
     *
     * try/catch 静默兜底：aegis 不存在或抛错都不影响业务。
     *
     * @param {string} fromLang 切换前语言
     * @param {string} toLang 切换后语言
     */
    reportLangChange(fromLang, toLang) {
      try {
        const { aegis } = this.$store.state.user;
        if (!aegis) return;

        // 翻译模式（模型 + 打包上限）
        // 当时区生效时，追加时区信息
        let mode = '';
        try {
          const timezone = (this.tzEnabled && this.tzTimezone && this.tzTimezone !== 'Asia/Shanghai')
            ? this.tzTimezone
            : '';
          mode = getTranslateMode(timezone) || '';
        } catch (e) { /* ignore */ }

        // 当前路由路径（优先 vue-router；兜底 window.location）
        let routePath = '';
        try {
          routePath = (this.$route && this.$route.path)
            || (typeof window !== 'undefined' && window.location && window.location.pathname)
            || '';
        } catch (e) { /* ignore */ }

        aegis.infoAll({
          msg: JSON.stringify({
            name: 'translate_lang_change',
            ext1: fromLang || '',
            ext2: toLang || '',
            ext3: `${mode}|${routePath}`,
            component: 'LanguageSwitcher',
            processDefinitionKey: this.processDefinitionKey || '',
            timestamp: new Date().toISOString(),
          }),
          ext1: 'translate-lang-change',
          trace: 'translate_lang_change',
        });
      } catch (e) {
        // 上报失败不影响业务
        // eslint-disable-next-line no-console
        console.warn('[LanguageSwitcher] Aegis 上报失败:', e);
      }
    },

    async onSelect(lang) {
      // 仅首次翻译期间锁定切换；之后允许并发切换（旧流程靠 epoch 自动作废）
      if (this.interactionLocked) return;
      this.visible = false;
      if (lang === this.currentLang) return;

      const fromLang = this.currentLang;
      this.pendingLang = lang;
      // 每次切换语言 → 新一轮翻译 → 显示 loading
      this.switchLoading = true;

      // 上报：用户切换语言（点击即上报，不依赖切换是否成功）
      this.reportLangChange(fromLang, lang);

      try {
        await pageTranslator.switchTo(lang);
        this.currentLang = lang;
        this.isRtl = isRTLLang(lang);
      } catch (e) {
        console.error('[LanguageSwitcher] 切换语言失败:', e);
      } finally {
        // 这一轮切换翻译完成 → 关闭 loading
        this.switchLoading = false;
        this.pendingLang = '';
      }
    },

    /**
     * 重新翻译当前语言
     * 强制清掉译文 + 缓存，重新走 API；用于模型偶发漏翻或译文异常时手动刷新
     */
    async onRetranslate() {
      if (this.interactionLocked) return;
      if (this.currentLang === SOURCE_LANG) return;
      this.visible = false;

      const curLang = this.currentLang;
      this.pendingLang = curLang;
      // 重新翻译也算一轮 → 显示 loading
      this.switchLoading = true;

      try {
        await pageTranslator.retranslate();
      } catch (e) {
        console.error('[LanguageSwitcher] 重新翻译失败:', e);
      } finally {
        this.switchLoading = false;
        this.pendingLang = '';
      }
    },
  },
};
</script>

<style lang="less" scoped>
@primary-color: #1677ff;
@primary-color-hover: #4096ff;
@text-primary: #1e293b;
@text-secondary: #64748b;
@bg-card: #ffffff;
@bg-section: #f8fafc;
@border-color: #e4e7ed;
@radius-md: 8px;
@radius-lg: 12px;
@shadow-base: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
@shadow-hover: 0 4px 12px -1px rgba(0, 0, 0, 0.08), 0 2px 4px -1px rgba(0, 0, 0, 0.04);
@shadow-ball: 0 4px 14px -2px rgba(22, 119, 255, 0.18), 0 2px 6px rgba(15, 23, 42, 0.08);
@shadow-ball-hover: 0 8px 22px -4px rgba(22, 119, 255, 0.28), 0 4px 10px rgba(15, 23, 42, 0.10);
@ease-standard: cubic-bezier(0.4, 0, 0.2, 1);
@ball-size: 40px;

/* 容器：fixed 定位，由 JS 计算 left/top；拖动期间禁用过渡 */
.lang-switcher {
  position: fixed;
  z-index: 999;
  width: @ball-size;
  height: @ball-size;
  transition: left 220ms @ease-standard, top 220ms @ease-standard;
  touch-action: none; // 防止 iOS 拖动球时整页滚动

  &.is-dragging {
    transition: none;
  }
}

/* 圆形悬浮球：白底 + 主色描边 + 地球图标 + 右上角语言角标 */
.lang-ball {
  position: relative;
  width: @ball-size;
  height: @ball-size;
  padding: 0;
  border: 1px solid rgba(22, 119, 255, 0.18);
  border-radius: 50%;
  background: #fff;
  color: @primary-color;
  cursor: grab;
  box-shadow: @shadow-ball;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  transition: transform 150ms @ease-standard, box-shadow 200ms @ease-standard, border-color 200ms @ease-standard;
  user-select: none;
  -webkit-tap-highlight-color: transparent;

  &:hover:not(:disabled) {
    box-shadow: @shadow-ball-hover;
    border-color: rgba(22, 119, 255, 0.4);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    cursor: grabbing;
    transform: scale(0.96);
  }

  &:focus-visible {
    outline: 2px solid @primary-color;
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.85;
  }

  .is-dragging & {
    cursor: grabbing;
    transform: scale(1.06);
    box-shadow: @shadow-ball-hover;
  }

  /* busy 态：外圈呼吸光晕，让用户感知"正在翻译" */
  .is-busy & {
    border-color: rgba(22, 119, 255, 0.6);
    animation: lang-pulse 1.6s ease-in-out infinite;
  }

  &__globe {
    width: 20px;
    height: 20px;
    color: @primary-color;
    flex-shrink: 0;
  }

  /* 右下角语言角标：极简胶囊 */
  &__badge {
    position: absolute;
    right: -2px;
    bottom: -2px;
    min-width: 18px;
    height: 16px;
    padding: 0 4px;
    font-size: 10px;
    line-height: 14px;
    font-weight: 600;
    letter-spacing: 0;
    color: #fff;
    background: @primary-color;
    border: 1.5px solid #fff;
    border-radius: 9999px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    pointer-events: none;
  }

  &__loader {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(22, 119, 255, 0.2);
    border-top-color: @primary-color;
    border-radius: 50%;
    animation: lang-spin 0.8s linear infinite;
  }
}

/* 透明遮罩：点击关闭面板 */
.lang-mask {
  position: fixed;
  inset: 0;
  z-index: 998;
  background: transparent;
}

/* 选项面板：基于球的位置自动选边 */
.lang-panel {
  position: absolute;
  z-index: 1000;
  min-width: 176px;
  background: @bg-card;
  border-radius: @radius-lg;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: @shadow-hover;
  overflow: hidden;

  /* 垂直方向 */
  &--bottom {
    top: calc(100% + 10px);
  }
  &--top {
    bottom: calc(100% + 10px);
  }

  /* 水平方向：按球中心位于视口左/右半决定对齐边 */
  &--left {
    left: 0;
  }
  &--right {
    right: 0;
  }

  &__title {
    padding: 10px 16px 8px;
    font-size: 12px;
    font-weight: 500;
    color: @text-secondary;
    background: @bg-section;
    border-bottom: 1px solid @border-color;
    letter-spacing: 0.3px;
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 4px 0;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    font-size: 14px;
    color: @text-primary;
    cursor: pointer;
    transition: background-color 150ms @ease-standard, color 150ms @ease-standard;

    &:hover,
    &:active {
      background: @bg-section;
    }

    &.is-active {
      color: @primary-color;
      font-weight: 500;
      background: rgba(22, 119, 255, 0.04);
    }

    &.is-disabled {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }

    // 正在切换到该项：高亮 + 允许显示 loading（不做整体半透明，让用户看到哪一个在忙）
    &.is-pending {
      opacity: 1;
      color: @primary-color;
      background: rgba(22, 119, 255, 0.06);
    }
  }

  &__check {
    color: @primary-color;
    font-size: 14px;
    font-weight: 600;
  }

  // 语言项右侧 + 重新翻译按钮内的转圈 loading
  &__spin {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid rgba(22, 119, 255, 0.25);
    border-top-color: @primary-color;
    border-radius: 50%;
    animation: lang-spin 600ms linear infinite;
    flex-shrink: 0;

    &--btn {
      width: 14px;
      height: 14px;
      border-width: 2px;
    }
  }

  &__name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // 底部"重新翻译"按钮区
  &__footer {
    padding: 8px 12px;
    border-top: 1px solid @border-color;
    background: @bg-section;
  }

  &__retry {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    padding: 8px 12px;
    border: 1px solid @border-color;
    border-radius: 6px;
    background: @bg-card;
    color: @text-primary;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 150ms @ease-standard;

    &:hover:not(.is-disabled) {
      border-color: @primary-color;
      color: @primary-color;
      background: rgba(22, 119, 255, 0.04);
    }

    &.is-disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    // busy 时边框强调 + 文案主色，传达"正在执行"
    &.is-loading {
      opacity: 1;
      border-color: @primary-color;
      color: @primary-color;
      background: rgba(22, 119, 255, 0.04);
      cursor: progress;
    }
  }

  &__retry-icon {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
}

.lang-fade-enter-active,
.lang-fade-leave-active {
  transition: opacity 200ms @ease-standard, transform 200ms @ease-standard;
}
.lang-fade-enter,
.lang-fade-leave-to {
  opacity: 0;
  transform: scale(0.96);
}

@keyframes lang-spin {
  to { transform: rotate(360deg); }
}

@keyframes lang-pulse {
  0%, 100% {
    box-shadow: @shadow-ball, 0 0 0 0 rgba(22, 119, 255, 0.35);
  }
  50% {
    box-shadow: @shadow-ball, 0 0 0 6px rgba(22, 119, 255, 0);
  }
}

/* 时区切换区域 */
.lang-panel__tz {
  border-top: 1px solid @border-color;
  padding: 8px 12px 10px;
}

.lang-panel__tz-title {
  font-size: 11px;
  font-weight: 600;
  color: @text-secondary;
  margin-bottom: 6px;
}

.lang-panel__tz-tabs {
  display: flex;
  align-items: center;
  background: #f1f5f9;
  border-radius: 16px;
  padding: 2px;
}

.lang-panel__tz-tab {
  flex: 1;
  padding: 5px 8px;
  border: none;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 500;
  color: @text-secondary;
  background: transparent;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.2s @ease-standard;
  -webkit-tap-highlight-color: transparent;

  &.is-active {
    background: @primary-color;
    color: #fff;
    box-shadow: 0 1px 4px rgba(22, 119, 255, 0.3);
  }

  &:not(.is-active):active {
    background: #e2e8f0;
  }
}

/* 时区 toast 提示 */
.lang-tz-toast {
  position: fixed;
  top: 50px;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 16px;
  background: rgba(0, 0, 0, 0.78);
  color: #fff;
  border-radius: 20px;
  font-size: 13px;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 9999;
}

.lang-tz-toast__text {
  line-height: 1.4;
}

.lang-tz-toast-enter-active,
.lang-tz-toast-leave-active {
  transition: all 0.3s ease;
}

.lang-tz-toast-enter,
.lang-tz-toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-10px);
}
</style>
